import parsePhoneNumber from 'libphonenumber-js';
import { Country, getCountryByTenant, supportedCountriesArray } from './countries';

export const phoneMasks = {
  US: '([000]) [000]-[0000]',
  BR: '([00]) [00000]-[0000]',
};

export const getPhoneNumberDetails = (phoneNumber: string) => {
  if (!phoneNumber) {
    return {
      phoneNumber,
      isValid: false,
    };
  }

  const phoneNumberObj = parsePhoneNumber(phoneNumber);

  const country: Country | undefined = supportedCountriesArray.find(
    (c) => c.alpha2Code === phoneNumberObj?.country,
  );

  return {
    phoneNumber,
    country,
    isValid: phoneNumberObj?.isValid(),
    nationalNumber: phoneNumberObj?.nationalNumber,
  };
};

/**
 * Removes all formatting from a phone number string, leaving only the "+" and the
 * space after the country code.
 */
export const unmaskPhone = (phoneNumber: string) => {
  // Using an arbitrary character (in this case '@') as placeholder for the first
  // space before removing all other spaces. This is to ensure that the position
  // of the first space is preserved.
  const phoneWithSpacePlaceholder = phoneNumber.replaceAll('@', '').replace(' ', '@');
  const phoneWithNoSpaces = phoneWithSpacePlaceholder.replaceAll(' ', '');
  const phoneWithFirstSpaceBack = phoneWithNoSpaces.replace('@', ' ');

  return phoneWithFirstSpaceBack.replaceAll(/[^+\d\s]/g, '');
};

export const unmaskPhoneCompletely = (phoneNumber: string) => {
  return unmaskPhone(phoneNumber).replaceAll(' ', '');
};

/**
 * Extracts the country code and national number from a phone number, removes any
 * formatting, and returns an array containing the country code and the national
 * number. Note that the phone number must have the country code separated from
 * the rest of the number by a space.
 */
export const getPhoneParts = (phoneNumber: string) => {
  const parts = unmaskPhone(phoneNumber).match(/^(\+\d+) ?(.*)/);
  const countryCode = parts?.[1] || getCountryByTenant().countryCode;
  const nationalNumber = parts?.[2] ?? '';

  return [countryCode, nationalNumber];
};

/**
 * Normalizes a phone number by separating the country code and national number
 * with a space. If the phone number is invalid, it returns a empty value.
 */
export const getNormalizedPhoneNumber = (phoneNumber: string) => {
  const unmaskedPhoneNumber = unmaskPhone(phoneNumber);
  const { country, nationalNumber, isValid } = getPhoneNumberDetails(unmaskedPhoneNumber);

  if (!isValid) {
    return '';
  }

  return `${country?.countryCode} ${nationalNumber}`;
};
